import {
  Button,
  ButtonDatePicker,
  Checkbox,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  TimePicker,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  VideoSmIcon,
} from '@crono-one/design-system';
import { Avatar } from '@mui/material';
import { FeConstants } from 'constants/FeConstants';
import { useAuth } from 'context/auth';
import { colors } from 'crono-fe-common/theme';
import { Account } from 'crono-fe-common/types/account';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { Event, EventOutcome } from 'crono-fe-common/types/event';
import { Prospect } from 'crono-fe-common/types/prospect';
import {
  fullName,
  getImageFromUrl,
  stringAvatarAccount,
} from 'crono-fe-common/utils';
import { FormikTouched, useFormik } from 'formik';
import useAccounts from 'hooks/services/account/useAccounts';
import { useLogEvent } from 'hooks/services/event/useLogEvent';
import { usePatchEvent } from 'hooks/services/event/usePatchEvent';
import useGetExternalProperty from 'hooks/services/externalProperty/useGetExternalProperty';
import useSearchAccountOpportunity from 'hooks/services/opportunity/useSearchAccountOpportunity';
import useSearchProspects from 'hooks/services/prospect/useSearchProspects';
import { useRoleCapabilities } from 'hooks/useRoleCapabilities';
import { OUTCOME_LABELS } from 'pages/accountDrawer/utils/meeting.constants';
import { getFirstError } from 'pages/taskBoard/upsertModal';
import { ProspectInfoField } from 'pages/taskBoard/upsertModal/ProspectInfoField';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { getDateYearsFromNow } from 'utils/dateUtils';
import { stringAvatarProspect } from 'utils/formatUtils';
import { cn } from 'utils/style-utils';
import AttendeesCombobox from './AttendeesCombobox';
import { MeetingExternalProperties } from './MeetingExternalProperties';
import {
  buildPatchEventPayload,
  getCreateMeetingFormDefaultValues,
  getDefaultMeetingTitle,
  getEditMeetingFormDefaultValues,
  getEventPrefillAccount,
  getEventPrefillOpportunity,
  getEventPrefillProspect,
  getMeetingFormValidationSchema,
  MeetingFormValues,
  resolveMeetingSpan,
  resolveSelectedAccount,
  resolveSelectedOpportunity,
  resolveSelectedProspect,
} from './model';

interface MeetingUpsertFormProps {
  initialAccount?: Account;
  initialProspect?: Prospect;
  initialOpportunity?: Opportunity;
  event?: Event;
  isDirtyRef: MutableRefObject<boolean>;
  onClose: () => void;
}

export function MeetingUpsertForm({
  initialAccount,
  initialProspect,
  initialOpportunity,
  event: fetchedEvent,
  isDirtyRef,
  onClose,
}: MeetingUpsertFormProps) {
  const { user, hasOpportunities } = useAuth();
  const { isManager } = useRoleCapabilities();

  const { mutate: logEvent, isPending: isLogEventPending } = useLogEvent();
  const { mutate: patchEvent, isPending: isPatchEventPending } =
    usePatchEvent();
  const isPending = isLogEventPending || isPatchEventPending;

  // The form is the meeting as it was when it opened. A later refetch of the same
  // event must not move the fields under an edit, nor shift the baseline the patch
  // diffs against — reopening the dialog is what mounts a new form on newer data.
  const [event] = useState(fetchedEvent);

  const isEditMode = !!event;
  const isCalendarSynced = !!event?.meeting;

  // Only Salesforce and HubSpot have custom fields on activities.
  const integrationType = user?.company?.integrationType;
  const hasCrmEventProperties =
    integrationType === IntegrationType.SALESFORCE ||
    integrationType === IntegrationType.HUBSPOT;

  const hasExternalProperties = !isEditMode && hasCrmEventProperties;

  const {
    data: externalPropertiesData,
    isFetchedAfterMount: isGetExternalPropertyFetchedAfterMount,
    isError: isGetExternalPropertyError,
  } = useGetExternalProperty({
    tableType: 'Event',
    enabled: hasExternalProperties,
    // Meeting external properties are currently created outside the app.
    // Moreover, a property mapped by another user, or an option a CRM picklist
    // gained, has to be there the next time the form opens.
    refetchOnMount: 'always',
  });

  // Every open re-reads the properties, and the cached ones are not shown until
  // this open has confirmed them: showing them first would swap them a moment
  // later for whatever the read brings back — its failure included.
  const isReadingExternalProperties =
    hasExternalProperties && !isGetExternalPropertyFetchedAfterMount;

  // The values are pushed to the CRM as the meeting is logged and never stored
  // in Crono, so an edit has nothing to show. A property that isn't synced
  // would be dropped, so no need for asking for it. A read that failed leaves
  // the previous properties behind, which are no longer known to be current.
  const externalProperties =
    isEditMode || isReadingExternalProperties || isGetExternalPropertyError
      ? []
      : (externalPropertiesData?.data?.data?.filter(
          (externalProperty) =>
            externalProperty.isSync && !externalProperty.isTag,
        ) ?? []);

  // A Boolean is never empty: its switch reads an absent value as off, which is
  // an answer, and demanding a value would take two toggles to give.
  const findMissingMandatoryProperty = (
    externalValues: Record<number, string>,
  ) =>
    externalProperties.find(
      (externalProperty) =>
        externalProperty.isMandatory &&
        externalProperty.valueType !== 'Boolean' &&
        !externalValues[externalProperty.id],
    );

  const prefillAccount = getEventPrefillAccount(event) ?? initialAccount;
  const prefillProspect = getEventPrefillProspect(event) ?? initialProspect;
  const prefillOpportunity =
    getEventPrefillOpportunity(event) ?? initialOpportunity;

  const userEditedTitleRef = useRef(isEditMode);

  const initialValues = isEditMode
    ? getEditMeetingFormDefaultValues({ event })
    : getCreateMeetingFormDefaultValues({
        initialAccount,
        initialProspect,
        initialOpportunity,
        currentUserEmail: user?.email,
      });

  const formik = useFormik<MeetingFormValues>({
    initialValues,
    validationSchema: getMeetingFormValidationSchema({
      isEditMode,
      isCalendarSynced,
    }),
    validateOnMount: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      if (isEditMode) {
        const payload = buildPatchEventPayload({
          eventId: event.id,
          values,
          initialValues,
          isCalendarSynced,
        });

        // There is something to send other than the id.
        if (Object.keys(payload).length > 1) {
          patchEvent(payload, { onSuccess: onClose });
        } else {
          // Otherwise just close the dialog.
          onClose();
        }
      } else {
        // Checked here rather than through the Create button: the properties
        // commit on blur, and a disabled button gets no pointer events, so
        // disabling it would swallow the click that commits the value.
        if (findMissingMandatoryProperty(values.externalValues)) {
          return;
        }

        const { startTime, endTime } = resolveMeetingSpan(values);

        const payload = {
          accountId: values.accountId!,
          prospectId: values.prospectId!,
          opportunityId: values.opportunityId,
          title: values.title,
          startTime,
          endTime,
          isAllDay: values.isAllDay,
          description: values.description || null,
          conferenceLink: values.conferenceLink || null,
          attendeeEmails: values.attendeeEmails,
          outcome: values.outcome || EventOutcome.Scheduled,
          externalValues: Object.keys(values.externalValues).length
            ? values.externalValues
            : undefined,
        };

        logEvent(payload, { onSuccess: onClose });
      }
    },
  });

  // Stable across renders (formik wraps it in useEventCallback), so a handler
  // built on it alone stays stable too.
  const { setFieldValue } = formik;

  // The dialog's own dismiss paths (Esc, backdrop, the header's close button) are
  // handled by the parent, which only asks to confirm when there is something to
  // lose. It reads the flag when the user acts, so an effect keeps it current.
  useEffect(() => {
    isDirtyRef.current = formik.dirty;

    return () => {
      isDirtyRef.current = false;
    };
  });

  const { data: accounts = [] } = useAccounts(
    {
      name: formik.values.searchAccountText,
      limit: 25,
      status: null,
      isManager,
    },
    0,
    !isEditMode,
    (response) => response?.data?.data ?? [],
  );

  const { data: prospects = [] } = useSearchProspects(
    {
      limit: 5,
      offset: 0,
      name:
        formik.values.searchProspectText === ''
          ? null
          : formik.values.searchProspectText,
      sort: 'Name',
      accountId: formik.values.accountId,
      onlyOwned: true,
      optedOut: false,
      userId: user?.id,
    },
    !isEditMode,
    (response) => response?.data?.data ?? [],
  );

  const selectedAccount = resolveSelectedAccount(
    formik.values.accountId,
    prefillAccount,
    prefillProspect,
    accounts,
  );

  const selectedProspect = resolveSelectedProspect(
    formik.values.prospectId,
    prefillProspect,
    prospects,
  );

  const { data: accountOpportunities = [] } = useSearchAccountOpportunity(
    {
      accountId: formik.values.accountId,
      limit: FeConstants.opportunitySearchLimit,
    },
    (response) => response?.data?.data ?? [],
  );

  // The query keeps serving the previous company's deals while it is disabled
  // for a null accountId, so without a company there is nothing to offer.
  const opportunities = formik.values.accountId ? accountOpportunities : [];

  const selectedOpportunity = resolveSelectedOpportunity(
    formik.values.opportunityId,
    prefillOpportunity,
    opportunities,
  );

  const showOpportunityField =
    hasOpportunities &&
    (!!formik.values.opportunityId ||
      (!isEditMode && opportunities.length > 0));

  const isFieldInvalid = (field: keyof MeetingFormValues) =>
    !!formik.errors[field] && !!formik.touched[field];

  // The properties are dynamic, so they are checked here rather than through the
  // validation schema: a schema rebuilt once they load wouldn't re-validate the
  // form until the next field change, leaving the error out of the tooltip.
  const missingMandatoryProperty = findMissingMandatoryProperty(
    formik.values.externalValues,
  );

  const errorMessage =
    getFirstError(formik.errors) ??
    (missingMandatoryProperty
      ? `${missingMandatoryProperty.publicName} is required`
      : null);

  // Apply several field changes in a single validation pass in order to avoid
  // concurrent async validations that race and leave formik.errors on a stale
  // snapshot.
  const applyChanges = (
    patch: Partial<MeetingFormValues>,
    touched?: FormikTouched<MeetingFormValues>,
  ) => {
    formik.setValues({ ...formik.values, ...patch }, true);
    if (touched) {
      formik.setTouched({ ...formik.touched, ...touched }, false);
    }
  };

  // Default title while the user hasn't typed their own.
  const getTitlePatch = (
    prospect?: Prospect | null,
  ): Partial<MeetingFormValues> => {
    if (userEditedTitleRef.current) {
      return {};
    }
    return { title: getDefaultMeetingTitle(prospect) };
  };

  // The contact attends the meeting, so picking one adds their email and
  // switching away drops the previous one — anything the user added by hand is
  // left alone.
  const getAttendeesPatch = (
    prospect?: Prospect | null,
  ): Partial<MeetingFormValues> => {
    const previousEmail = selectedProspect?.email;
    const nextEmail = prospect?.email;

    if (previousEmail === nextEmail) {
      return {};
    }

    const attendeeEmails = formik.values.attendeeEmails.filter(
      (email) => email !== previousEmail,
    );
    if (nextEmail && !attendeeEmails.includes(nextEmail)) {
      attendeeEmails.push(nextEmail);
    }

    return { attendeeEmails };
  };

  const handleChangeAccount = (value: string | number | null) => {
    const accountId = value == null ? null : String(value);

    const patch: Partial<MeetingFormValues> = {
      accountId,
      ...getTitlePatch(null),
    };
    const touched: FormikTouched<MeetingFormValues> = {
      accountId: true,
    };

    if (!accountId) {
      patch.searchAccountText = '';
    }

    // Changing the account clears the selected prospect.
    if (formik.values.prospectId) {
      patch.prospectId = null;
      patch.searchProspectText = '';
      touched.prospectId = true;
      Object.assign(patch, getAttendeesPatch(null));
    }

    // A deal belongs to one company, so it cannot survive the company changing.
    if (formik.values.opportunityId) {
      patch.opportunityId = null;
      patch.searchOpportunityText = '';
    }

    applyChanges(patch, touched);
  };

  const handleChangeContact = (value: string | number | null) => {
    const prospectId = value == null ? null : String(value);
    const prospect = prospects.find((el) => el.objectId === value);

    const patch: Partial<MeetingFormValues> = {
      prospectId,
      searchProspectText: '',
      ...getTitlePatch(prospect),
      ...getAttendeesPatch(prospect),
    };
    const touched: FormikTouched<MeetingFormValues> = {
      prospectId: true,
    };

    // Mirror the account to the prospect's account, or reset it if the prospect
    // has none.
    if (prospect) {
      const prospectAccountId =
        prospect.account?.objectId ?? prospect.accountId ?? null;
      patch.accountId = prospectAccountId;
      patch.searchAccountText = prospectAccountId
        ? (prospect.account?.name ?? '')
        : '';
      touched.accountId = true;

      // The mirrored account can be another one, taking its deals with it.
      if (
        formik.values.opportunityId &&
        prospectAccountId !== formik.values.accountId
      ) {
        patch.opportunityId = null;
        patch.searchOpportunityText = '';
      }
    }

    applyChanges(patch, touched);
  };

  const handleChangeDeal = (value: string | number | null) => {
    applyChanges({
      opportunityId: value == null ? null : String(value),
      searchOpportunityText: '',
    });
  };

  const handleChangeTitle = (value: string) => {
    formik.setFieldValue('title', value, true);
    formik.setFieldTouched('title', true, false);
    userEditedTitleRef.current = true;
  };

  // An all-day meeting carries no time of day, so checking the box drops both
  // times — they would otherwise be sent back the next time the span changes.
  const handleChangeAllDay = (isAllDay: boolean) => {
    applyChanges(
      isAllDay ? { isAllDay, startTime: '', endTime: '' } : { isAllDay },
    );
  };

  // An emptied property is dropped rather than sent as an empty string: the two
  // mean the same to the backend, but a leftover key would make the form dirty.
  // formik's setIn deletes the path when the value is undefined.
  const handleChangeExternalValue = useCallback(
    (externalPropertyId: number, value: string) => {
      setFieldValue(
        `externalValues.${externalPropertyId}`,
        value || undefined,
        false,
      );
    },
    [setFieldValue],
  );

  return (
    <form
      className="flex-1 min-h-0 flex flex-col gap-6"
      onSubmit={formik.handleSubmit}
    >
      <DialogHeader>
        <DialogTitle>
          {isEditMode ? 'Edit meeting' : 'Log a meeting'}
        </DialogTitle>
      </DialogHeader>

      <DialogBody className="gap-4">
        <div className="grid grid-cols-2 gap-8">
          <ProspectInfoField
            id="account"
            label="Company*"
            placeholder="Type company name..."
            noOptionsText="No owned companies"
            searchText={formik.values.searchAccountText}
            onSearchTextChange={(text) =>
              formik.setFieldValue('searchAccountText', text, false)
            }
            invalid={isFieldInvalid('accountId')}
            disabled={isEditMode}
            hideRemoveButton={isEditMode}
            value={formik.values.accountId}
            onChange={handleChangeAccount}
            options={accounts}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.objectId}
            selectedValueSlot={
              selectedAccount && (
                <div className="flex items-center gap-2 overflow-hidden">
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: colors.primaryLight,
                      color: colors.primary,
                      fontSize: 14,
                    }}
                    src={
                      selectedAccount.website &&
                      getImageFromUrl(selectedAccount.website, null)
                    }
                  >
                    {stringAvatarAccount(selectedAccount.name ?? '')}
                  </Avatar>

                  <div className="text-sm font-medium truncate">
                    {selectedAccount.name}
                  </div>
                </div>
              )
            }
          />

          <ProspectInfoField
            id="prospect"
            label="Contact*"
            placeholder="Type contact name..."
            noOptionsText="No owned contacts"
            searchText={formik.values.searchProspectText}
            onSearchTextChange={(text) =>
              formik.setFieldValue('searchProspectText', text, false)
            }
            invalid={isFieldInvalid('prospectId')}
            disabled={isEditMode}
            hideRemoveButton={isEditMode}
            value={formik.values.prospectId}
            onChange={handleChangeContact}
            options={prospects}
            getOptionLabel={(option) =>
              option.name ||
              fullName(option.firstName, option.lastName) ||
              option.email ||
              'No name'
            }
            getOptionValue={(option) => option.objectId}
            selectedValueSlot={
              selectedProspect && (
                <div className="flex items-center gap-2 overflow-hidden">
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: colors.primaryLight,
                      color: colors.primary,
                      fontSize: 14,
                    }}
                  >
                    {stringAvatarProspect(selectedProspect)}
                  </Avatar>

                  <div className="flex flex-col">
                    <p
                      className={cn(
                        'font-medium truncate',
                        selectedProspect.title ? 'text-xs' : 'text-sm',
                      )}
                    >
                      {selectedProspect.name ?? ''}
                    </p>
                    <p className="text-3xs text-gray-700 truncate">
                      {selectedProspect.title ?? ''}
                    </p>
                  </div>
                </div>
              )
            }
          />
        </div>

        {showOpportunityField && (
          <ProspectInfoField
            id="deal"
            label="Deal name"
            placeholder="Type deal name..."
            noOptionsText="No deals found"
            searchText={formik.values.searchOpportunityText}
            onSearchTextChange={(text) =>
              formik.setFieldValue('searchOpportunityText', text, false)
            }
            disabled={isEditMode}
            hideRemoveButton={isEditMode}
            value={formik.values.opportunityId}
            onChange={handleChangeDeal}
            options={opportunities}
            getOptionLabel={(option) => option.name ?? ''}
            getOptionValue={(option) => option.objectId ?? ''}
            selectedValueSlot={
              selectedOpportunity && (
                <div className="flex items-center gap-2 overflow-hidden pl-1">
                  <span className="text-sm font-medium truncate">
                    {selectedOpportunity.name}
                  </span>
                </div>
              )
            }
          />
        )}

        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-semibold text-gray-900"
            htmlFor="title"
          >
            Title*
          </label>
          <Input
            id="title"
            placeholder="Type a title..."
            dsSize="sm"
            aria-invalid={isFieldInvalid('title')}
            disabled={isCalendarSynced}
            value={formik.values.title}
            onChange={(event) => handleChangeTitle(event.target.value)}
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">Info*</span>

            <label className="flex items-center gap-1 text-xs font-medium text-gray-700">
              <Checkbox
                checked={formik.values.isAllDay}
                disabled={isCalendarSynced}
                onCheckedChange={(checked) =>
                  handleChangeAllDay(checked === true)
                }
              />
              <span>All day</span>
            </label>
          </div>

          <div className="flex flex-col rounded-lg border border-neutral-400">
            <div className="flex items-center justify-between gap-6 p-2 rounded-t-lg border-b border-neutral-400 bg-neutral-100">
              <div className="flex-1 flex items-center gap-2">
                <div className="size-8 rounded-full flex items-center justify-center bg-yellow-500 text-white">
                  <VideoSmIcon className="size-4" />
                </div>
                <div className="flex-1 h-8 flex items-center px-2 rounded-lg border border-neutral-300 bg-white text-sm">
                  Meeting
                </div>
              </div>

              <div className="flex-1 flex items-center gap-1">
                <ButtonDatePicker
                  aria-label="Date"
                  variant="outline"
                  size="sm"
                  contentClassName="z-50000"
                  mode="single"
                  triggerDisabled={isCalendarSynced}
                  selected={formik.values.date}
                  startMonth={getDateYearsFromNow(
                    -FeConstants.datePickerDefaultYearsOffset,
                  )}
                  endMonth={getDateYearsFromNow(
                    +FeConstants.datePickerDefaultYearsOffset,
                  )}
                  onSelect={(date) => {
                    if (date) {
                      formik.setFieldValue('date', date, true);
                    }
                  }}
                />

                <TimePicker
                  id="startTime"
                  placeholder="From"
                  aria-label="From"
                  showTrigger={false}
                  dsSize="sm"
                  className="w-20 max-w-20 min-w-20 flex items-center gap-2 py-1 pl-1 pr-1.5 *:p-0! *:last:hidden"
                  aria-invalid={isFieldInvalid('startTime')}
                  disabled={formik.values.isAllDay || isCalendarSynced}
                  value={formik.values.startTime}
                  onValueChange={(time) => {
                    formik.setFieldValue('startTime', time ?? '', true);
                    formik.setFieldTouched('startTime', true, false);
                  }}
                />

                <TimePicker
                  id="endTime"
                  placeholder="To"
                  aria-label="To"
                  showTrigger={false}
                  dsSize="sm"
                  className="w-20 max-w-20 min-w-20 flex items-center gap-2 py-1 pl-1 pr-1.5 *:p-0! *:last:hidden"
                  aria-invalid={isFieldInvalid('endTime')}
                  disabled={formik.values.isAllDay || isCalendarSynced}
                  value={formik.values.endTime}
                  onValueChange={(time) => {
                    formik.setFieldValue('endTime', time ?? '', true);
                    formik.setFieldTouched('endTime', true, false);
                  }}
                />
              </div>
            </div>

            <Textarea
              placeholder="Add description..."
              aria-label="Description"
              variant="text"
              className="w-full min-w-full max-w-full p-2"
              disabled={isCalendarSynced}
              value={formik.values.description}
              onChange={(event) =>
                formik.setFieldValue('description', event.target.value, false)
              }
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-gray-900" htmlFor="link">
            Link
          </label>
          <Input
            id="link"
            placeholder="Type"
            dsSize="sm"
            disabled={isCalendarSynced}
            value={formik.values.conferenceLink}
            onChange={(event) =>
              formik.setFieldValue('conferenceLink', event.target.value, false)
            }
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-semibold text-gray-900"
            htmlFor="attendees"
          >
            Attendees
          </label>
          <AttendeesCombobox
            id="attendees"
            accountId={formik.values.accountId}
            disabled={isCalendarSynced}
            values={formik.values.attendeeEmails}
            onChange={(emails) =>
              formik.setFieldValue('attendeeEmails', emails, false)
            }
          />
        </div>

        <div className="flex flex-col gap-1">
          <label
            className="text-sm font-semibold text-gray-900"
            htmlFor="outcome"
          >
            Outcome
          </label>
          <Select
            value={formik.values.outcome}
            onValueChange={(value) =>
              formik.setFieldValue('outcome', value as EventOutcome, false)
            }
          >
            <SelectTrigger id="outcome" size="sm" className="w-full">
              <SelectValue placeholder="-" />
            </SelectTrigger>
            <SelectContent className="z-50000">
              <SelectGroup>
                {Object.values(EventOutcome).map((outcome) => (
                  <SelectItem key={outcome} value={outcome}>
                    {OUTCOME_LABELS[outcome]}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <MeetingExternalProperties
          isLoading={isReadingExternalProperties}
          externalProperties={externalProperties}
          values={formik.values.externalValues}
          onChange={handleChangeExternalValue}
        />
      </DialogBody>

      <DialogFooter>
        <Button
          variant="outline"
          size="lg"
          className="min-w-30"
          disabled={isPending}
          onClick={onClose}
        >
          Cancel
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="submit"
              size="lg"
              className="min-w-30"
              loading={isPending}
              disabled={!formik.isValid || isReadingExternalProperties}
            >
              {isEditMode ? 'Save' : 'Create'}
            </Button>
          </TooltipTrigger>
          {errorMessage && <TooltipContent>{errorMessage}</TooltipContent>}
        </Tooltip>
      </DialogFooter>
    </form>
  );
}
